/* global fbq */
/**
 *
 *  Lifestyle landing page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 15/04/2024.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import Slider from "react-slick";
import Modal from "react-modal";
import Carousel from "react-gallery-carousel";
import { Title, Meta, Link as LinkMeta } from "react-meta-elements";

import { useAppState } from "../../state";
import { useRegion } from "../../context/RegionProvider";

// Components
import Header from "../Homepage/Header/Header";
import Footer from "../Footer/Footer";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";
import Button from "../Button/Button";
import AskConciergeModal from "../Homepage/AskConciergeModal/AskConciergeModal";
import MembershipBox from "./MembershipBox/MembershipBox";
import ManageMembershipModal from "./ManageMembershipModal/ManageMembershipModal";
import CancelMembershipModal from "../CancelMembershipModal/CancelMembershipModal";
import PauseMembershipModal from "./MembershipBox/PauseMembershipModal";
// import OfferCard from "./OfferCard/OfferCard";
import TruBeInPress from "../TruBeInPress/TruBeInPress";
import SliderPrevArrow from "../SliderPrevButton/SliderPrevButton";
import SliderNextArrow from "../SliderNextButton/SliderNextButton";
import UpcomingExperiencesSlider from "./UpcomingExperiencesSlider/UpcomingExperiencesSlider";
import ElevatedClassesSlider from "./ElevatedClassesSlider/ElevatedClassesSlider";
import OffersSlider from "./OffersSlider/OffersSlider";
import SpecialOfferBox from "./SpecialOfferBox/SpecialOfferBox";
import AppBanner from "../AppBanner/AppBanner";
import LogoBar from "../LogoBar/LogoBar";
import MovingBar from "../MovingBar/MovingBar";
import HeadlineWithSeparator from "../HeadlineWithSeparator/HeadlineWithSeparator";
import TitleWithSeparator from "../TitleWithSeparator/TitleWithSeparator";
import {
  ExploreCard,
  BenefitCard,
} from "../UKComponents/LifestyleLp/LifestyleLp";
import GatedEventCard from "./GatedEventCard/GatedEventCard";
import { YouTubeVideoSlider } from "./YouTubeVideoSlider/YouTubeVideoSlider";
import { ProgressiveImage } from "../ProgressiveImage/ProgressiveImage";
import CityPickerButton from "../CityPickerButton/CityPickerButton";
import { Skeleton } from "../Skeleton/Skeleton";
import { SeriesOfClassesSlider } from "./SeriesOfClassesSlider/SeriesOfClassesSlider";
import { EventSeriesSlide } from "../SeriesList/EventSeriesSlide/EventSeriesSlide";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";
import { useGetPaymentItemsPublic } from "../../hooks/usePayments/usePayments";
import { useGetPayments } from "../../hooks/usePayments/usePayments";
import { useGetOffers } from "../../hooks/useOffers/useOffers";
import {
  useGetEvents,
  useGetSeriesPublic,
  useGetEventSeries,
  useGetSeries,
  useInvalidateSeries,
} from "../../hooks/useEvents/useEvents";

import useCurrencyFormat from "../../hooks/useCurrencyFormat/useCurrencyFormat";

// Utils
import { clientWidth, cn, getPartnerPictureUrl } from "../../utils";
import { CITY_MAPPING, modalStyles } from "../../constants";
import {
  getMembershipSubscriptionItem,
  getSingleTicketItem,
} from "../../utils/payment";

// Icons
// import ArrowLeftIcon from "../../icons/ArrowLeftIcon";
import ArrowRightIcon from "../../icons/ArrowRightIcon";
import CaretDownIcon from "../../icons/CaretDownIcon";
import CaretUpIcon from "../../icons/CaretUpIcon";
import CloseIcon from "../../icons/CloseIcon";
import CheckboxOIcon from "../../icons/CheckboxOIcon";
import BlockerIcon from "../../icons/BlockerIcon";
import CheckNiceIcon from "../../icons/CheckNiceIcon";
// import HeartIcon from "../Homepage/HeartIcon";
// import HighQualityIcon from "../Homepage/HighQualityIcon";
// import LocationIcon from "../Homepage/LocationIcon";
// import OneOnOneSessionsIcon from "../Homepage/OneOnOneSessionsIcon";
// import MapMarkerIcon from "../../icons/MapMarkerIcon";
import KeyIcon from "../../icons/KeyIcon";

// Images
import lifestyle1 from "./lifestyle-carousel-slide-1.jpg";
import lifestyle2 from "./lifestyle-carousel-slide-2.jpg";
import lifestyle3 from "./lifestyle-carousel-slide-3.jpg";
import lifestyle4 from "./lifestyle-carousel-slide-4.jpg";
import lifestyle5 from "./lifestyle-carousel-slide-5.jpg";
import lifestyle6 from "./lifestyle-carousel-slide-6.jpg";
import lifestyle7 from "./lifestyle-carousel-slide-7.jpg";
import lifestyle8 from "./lifestyle-carousel-slide-8.jpg";
import lifestyle9 from "./lifestyle-carousel-slide-9.jpg";
import lifestyle10 from "./lifestyle-carousel-slide-10.jpg";

import lifestyleCollectiveLogo from "../Homepage/lifestyle-collective.png";
import lifestyleCollectiveLogo2x from "../Homepage/lifestyle-collective@2x.png";
import lifestyleCollectiveLogo3x from "../Homepage/lifestyle-collective@3x.png";

import exploreCardImg1 from "../UKComponents/LifestyleLp/explore-card-pilates.png";
import exploreCardImg12x from "../UKComponents/LifestyleLp/explore-card-pilates@2x.png";

import exploreCardImg2 from "../UKComponents/LifestyleLp/explore-card-sound-healing-and-meditation.png";
import exploreCardImg22x from "../UKComponents/LifestyleLp/explore-card-sound-healing-and-meditation@2x.png";

import exploreCardImg3 from "../UKComponents/LifestyleLp/explore-card-strength-and-mobility.png";
import exploreCardImg32x from "../UKComponents/LifestyleLp/explore-card-strength-and-mobility@2x.png";

import exploreCardImg4 from "../UKComponents/LifestyleLp/explore-card-yoga.png";
import exploreCardImg42x from "../UKComponents/LifestyleLp/explore-card-yoga@2x.png";

import benefitsExclusiveVenues from "./benefit-card-exclusive-venues.jpg";
import benefitsExclusiveVenues2x from "./benefit-card-exclusive-venues@2x.jpg";

import benefitsClassesByTopCoaches from "./benefit-card-classes-by-top-coaches.jpg";
import benefitsClassesByTopCoaches2x from "./benefit-card-classes-by-top-coaches@2x.jpg";

import benefitsBrandCollabsGifts from "../UKComponents/LifestyleLp/benefit-card-brand-collabs-gifts.jpg";
import benefitsBrandCollabsGifts2x from "../UKComponents/LifestyleLp/benefit-card-brand-collabs-gifts@2x.jpg";

import benefitsPostWorkoutBrandActivations from "../UKComponents/LifestyleLp/benefit-card-post-workout-brand-activations.jpg";
import benefitsPostWorkoutBrandActivations2x from "../UKComponents/LifestyleLp/benefit-card-post-workout-brand-activations@2x.jpg";

import askConcierge from "../Homepage/AskConciergeModal/ask-concierge.svg";
import defaultPartnerPhoto from "../../images/generic-trainerphoto.svg";

import Lottie from "react-lottie";
import animationData from "../../lotties/loading-spinner";
import TruBe from "../Homepage/TruBe";
import TruBeLogo from "../../icons/TruBeLogo";
import { EventRatingModal } from "../EventRatingModal/EventRatingModal";
import { getBuyMembershipPath } from "../../shared/router";
import { WaitFilledIcon } from "../../shared/ui/wait-filled-icon";

const INFO_MODAL_HIDE_TIMEOUT = 5000;

const communitySliderImages = [
  lifestyle1,
  lifestyle2,
  lifestyle3,
  lifestyle4,
  lifestyle5,
  lifestyle6,
  lifestyle7,
  lifestyle8,
  lifestyle9,
  lifestyle10,
];

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

const sliderResponsiveSettings = [
  {
    breakpoint: 640,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
  {
    breakpoint: 960,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
  {
    breakpoint: 1400,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
  {
    breakpoint: 1600,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
  {
    breakpoint: 5000,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
];

const AccordionItem = ({ className, title, text }) => {
  const [collapsed, setCollapsed] = useState(true);

  const handleClick = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed]);

  return (
    <div className="tbk-mb-2 tbk-border-b tbk-border-solid tbk-border-blue-grey-light">
      <button
        className={`${
          className ? `${className} ` : ""
        }tbk-flex tbk-w-full tbk-items-start tbk-justify-between tbk-pb-1 tbk-text-primary hover:tbk-text-coral`}
        onClick={handleClick}
      >
        <div className="tbk-text-h3-subtitle tbk-grow tbk-text-left">
          {title}
        </div>
        {collapsed ? <CaretDownIcon size={32} /> : <CaretUpIcon />}
      </button>
      <div
        className="tbk-text-main tbk-overflow-hidden tbk-text-left tbk-text-primary tbk-transition-height"
        style={{
          height: collapsed ? "0" : "auto",
          marginBottom: collapsed ? "0" : "24px",
        }}
      >
        {text}
      </div>
    </div>
  );
};

export const EventCard = ({
  item: eventData,
  to,
  imgClassName,
  showRsvpCta,
  hideRsvp,
  onRsvpCtaClick,
}) => {
  return to || showRsvpCta ? (
    <Link
      to={to || `/events/${eventData.id}`}
      // to={`/events/${evt.id}`}
      // href={`https://trubeapp.com/app-redirects/deeplink-redirect-events.html?id=${evt.id}`}
      // target="_blank"
      className="tbk-mb-2 lg:tbk-mb-0"
      onClick={onRsvpCtaClick}
    >
      <div className="tbk-relative">
        <div className="tbk-absolute tbk-right-3 tbk-top-0 tbk-z-1 tbk-text-right">
          {eventData.capacity === eventData.currentCapacity ? (
            <div className="tbk-text-main tbk-rounded-b-lg tbk-bg-primary tbk-px-1 tbk-text-basic-white">
              Sold Out
            </div>
          ) : null}
          {eventData.joined ? (
            <CheckNiceIcon
              width={24}
              height={24}
              className="tbk-mt-3 tbk-inline-block tbk-rounded-full tbk-border tbk-border-basic-white tbk-bg-green tbk-text-basic-white"
            />
          ) : (
            eventData.waitlisted && (
              <WaitFilledIcon
                width={24}
                height={24}
                className="tbk-ml-1 tbk-mt-3 tbk-inline-block tbk-text-primary"
              />
            )
          )}
        </div>
        {eventData.id === "elevated" ? (
          <GatedEventCard
            className={`${
              imgClassName ? `${imgClassName} ` : ""
            } tbk-mb-2 tbk-w-full`}
          />
        ) : (
          <ProgressiveImage
            className={cn(
              "tbk-relative tbk-mb-2 tbk-w-full tbk-overflow-hidden tbk-rounded-2xl tbk-object-cover tbk-object-left-top",
              imgClassName
            )}
            src={eventData.coverPhoto}
            //srcSet={`${access2xImg} 2x, ${access3xImg} 3x`}
            imgConfig={[]}
            alt="Upcoming Fitness and wellbeing classes"
            logoHeight={56}
            imgStyle={{
              width: "100%",
              height: "100%",
            }}
          />
        )}
        {showRsvpCta && !hideRsvp ? (
          <Button
            variant="secondary"
            size="md"
            className="tbk-w-full"
            endIcon={<ArrowRightIcon />}
          >
            RSVP
          </Button>
        ) : null}
      </div>
    </Link>
  ) : (
    <div className="tbk-relative">
      <div className="tbk-absolute tbk-right-3 tbk-top-0 tbk-text-right">
        {eventData.capacity === eventData.currentCapacity ? (
          <div className="tbk-text-main tbk-rounded-b-lg tbk-bg-primary tbk-px-1 tbk-text-basic-white">
            Sold Out
          </div>
        ) : null}
        {eventData.joined && (
          <CheckNiceIcon
            width={24}
            height={24}
            className="tbk-mt-3 tbk-inline-block tbk-rounded-full tbk-border tbk-border-basic-white tbk-bg-green tbk-text-basic-white"
          />
        )}
      </div>
      {eventData.id === "elevated" ? (
        <GatedEventCard
          className={`${
            imgClassName ? `${imgClassName} ` : ""
          } tbk-mb-2 tbk-w-full`}
        />
      ) : (
        <img
          className={`${
            imgClassName ? `${imgClassName} ` : ""
          }tbk-mb-2 tbk-w-full tbk-overflow-hidden tbk-rounded-2xl tbk-object-cover tbk-object-left-top`}
          src={eventData.coverPhoto}
          //srcSet={`${access2xImg} 2x, ${access3xImg} 3x`}
          alt="Upcoming Fitness and wellbeing classes"
        />
      )}
      {showRsvpCta ? (
        <Button
          variant="secondary"
          size="md"
          className="tbk-w-full"
          endIcon={<ArrowRightIcon />}
        >
          RSVP
        </Button>
      ) : null}
    </div>
  );
};

export default function LifestyleLandingPage() {
  const location = useLocation();
  const { theme } = useTheme();
  const { region, city } = useRegion();

  const {
    user,
    hasTriedToAutoLogin,
    isTrialAvailable,
    partnerLoaded,
    partnerSummary,
    cancelEventMembership,
    pauseEventMembership,
    unpauseEventMembership,
    //checkTrialAvailable,
    setPromoCode,
    clearUpcomingEventsLoaded,
    clearMembershipItemsLoaded,
    error,
  } = useAppState();

  const { events: upcomingEvents, isPending: isUpcomingEventsLoading } =
    useGetEvents({
      customerId: user?.id,
      region: user ? user.region : region,
      city: user?.city || city,
      status: ["UPCOMING", "STARTED", "UPCOMING_FULL"],
      size: 8,
      sort: "start,asc",
      enabled: hasTriedToAutoLogin,
    });

  const { events: elevatedEvents, isPending: isElevatedEventsLoading } =
    useGetEvents({
      customerId: user?.id,
      region: user ? user.region : region,
      city: user?.city || city,
      status: ["UPCOMING", "STARTED", "UPCOMING_FULL"],
      tier: [
        "FIRST_TIER",
        "SECOND_TIER",
        "THIRD_TIER",
        "FOURTH_TIER",
        "FIFTH_TIER",
      ],
      size: 8,
      sort: "start,asc",
      enabled: hasTriedToAutoLogin,
    });

  const { events: completedEvents, isPending: isCompletedEventsLoading } =
    useGetEvents({
      customerId: user?.id,
      region: user ? user.region : region,
      city: user?.city || city,
      status: ["COMPLETED"],
      size: 4,
      sort: "start,desc",
      enabled: hasTriedToAutoLogin && !!user?.id, // Только для аутентифицированных пользователей
    });

  const { data: customerMembership, isLoading: isMembershipLoading } =
    useGetPayments(
      {
        customerId: user?.id,
        type: "EVENT_MEMBERSHIP",
        page: 0,
        size: 1,
        sort: "id,asc",
      },
      {
        enabled: hasTriedToAutoLogin && !!user?.id,
      }
    );

  const { data: offersData, isLoading: isOffersLoading } = useGetOffers(
    { region: user ? user.region : region },
    { enabled: hasTriedToAutoLogin }
  );

  const { data: membershipItems, isLoading: isMembershipItemsLoading } =
    useGetPaymentItemsPublic(
      { type: "EVENT_MEMBERSHIP,EVENT", city, region },
      { enabled: hasTriedToAutoLogin }
    );

  const { data: seriesData, isLoading: isSeriesLoading } = useGetSeriesPublic({
    user,
    region,
    city: user && user.city ? user.city : city,
  });

  const [showMoreQuestions, setShowMoreQuestions] = useState(false);
  const [autoPlay, setAutoPlay] = useState(true);
  const [hasMembershipBeenCancelled, setHasMembershipBeenCancelled] =
    useState(false);
  // const [hasMembershipBeenPaused, setHasMembershipBeenPaused] = useState(false);
  const [isGalleryCarouselModalOpen, setIsGalleryCarouselModalOpen] =
    useState(null);
  const [isCancelSubscriptionModalOpen, setIsCancelSubscriptionModalOpen] =
    useState(false);
  const [isPauseSubscriptionModalOpen, setIsPauseSubscriptionModalOpen] =
    useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalTitle, setInfoModalTitle] = useState("");
  const [infoModalMsg, setInfoModalMsg] = useState("");
  const [galleryImages, setGalleryImages] = useState([
    ...communitySliderImages,
  ]);

  const [galleryInitialSlide, setGalleryInitialSlide] = useState(0);
  // const [height, setHeight] = useState(
  //   clientWidth() > theme.responsive.breakpoints.xl
  //     ? 496
  //     : clientWidth() > theme.responsive.breakpoints.md
  //     ? 405
  //     : 205
  // );

  const heroRef = React.useRef(null);
  const infoModalResolveRef = useRef(null);
  const cityPickerResolveRef = useRef(null);
  const isCityPickerModalOpenRef = useRef(false);
  const questionsSectionRef = useRef();

  const [isAskConciergeModalOpen, setIsAskConciergeModalOpen] = useState(false);
  const [isManageMembershipModalOpen, setIsManageMembershipModalOpen] =
    useState(false);
  const [isCityPickerModalOpen, setIsCityPickerModalOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);

  const series = seriesData?.data?.content || [];
  const offers = offersData?.data || [];
  const membership = customerMembership?.data?.content?.[0] || null;

  const isUserLoggedIn = !!(user && user.id);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const promoCode = query.get("promocode");

    if (promoCode) {
      setPromoCode(promoCode);
      // Referral code is valid
      openInfoModal(
        `Your booking is linked to a TruBe ambassador. Continue to confirm your session`,
        "Ambassador Link Activated",
        true
      ).then(() => {
        setIsInfoModalOpen(false);
      });
    }
  }, [location]);

  const handleMoreQuestionsClick = useCallback(() => {
    setShowMoreQuestions(true);
  }, []);

  const handleSlideSwipe = useCallback(
    (oldIndex, newIndex) => {
      if (autoPlay) {
        setAutoPlay(false);
      }
    },
    [autoPlay]
  );

  const openGalleryCarouselModal = (index) => {
    setGalleryImages([...communitySliderImages]);
    setIsGalleryCarouselModalOpen(true);
    setGalleryInitialSlide(index);
  };

  const closeGalleryCarouselModal = () => {
    setIsGalleryCarouselModalOpen(false);
  };

  const openCancelSubscriptionModal = useCallback(() => {
    setIsCancelSubscriptionModalOpen(true);
  }, []);

  const closeCancelSubscriptionModal = useCallback(() => {
    setIsCancelSubscriptionModalOpen(false);
  }, []);

  const openPauseSubscriptionModal = useCallback(() => {
    setIsPauseSubscriptionModalOpen(true);
  }, []);

  const closePauseSubscriptionModal = useCallback(() => {
    setIsPauseSubscriptionModalOpen(false);
  }, []);

  const openInfoModal = useCallback(
    (msg, title = "Sorry", autoHide = false) => {
      return new Promise((resolve, reject) => {
        infoModalResolveRef.current = resolve;
        setIsInfoModalOpen(true);
        setInfoModalTitle(title);
        setInfoModalMsg(msg);

        if (autoHide) {
          setTimeout(() => resolve(), INFO_MODAL_HIDE_TIMEOUT);
        }
      });
    },
    []
  );

  const closeInfoModal = useCallback(() => {
    infoModalResolveRef.current?.();
    infoModalResolveRef.current = null;
    setIsInfoModalOpen(false);
  }, []);

  const openCityPickerModal = useCallback(() => {
    // eslint-disable-next-line
    return new Promise((resolve, reject) => {
      cityPickerResolveRef.current = resolve;

      setIsCityPickerModalOpen(true);
      isCityPickerModalOpenRef.current = true;

      // Analytics.trackEvent(TRAINERS_LIST_PAGE_CATEGORY, 'Open', EVENT_LOCATION_PICKER_OPEN);
    });
  }, []);

  // `closeCityPickerModal` callback should go before `useBodyScrollLock` hook,
  // because of `setShowAllGyms(false)` call, otherwise closing the modal will block the scroll
  // and `useBodyScrollLock` won't have a chance to "unblock" it

  /*
   * After closing the Location Selector Modal (promise resolved) the booking flow continues.
   * @param bail -- whether to skip the booking flow
   */
  // const closeCityPickerModal = useCallback(() => {
  //   setIsCityPickerModalOpen(false);
  //   isCityPickerModalOpenRef.current = false;
  // }, [selectedCity]);
  //
  // const handleCityClick = useCallback(() => {
  //   //openRegionSelectModal();
  //   setIsCityPickerModalOpen(true);
  // }, []);

  const pastEventsVideos = [
    "https://www.youtube.com/embed/dEDyOkviibA?feature=oembed&enablejsapi=1",
    "https://www.youtube.com/embed/Ikv2tbbEgXg?feature=oembed&enablejsapi=1",
    "https://www.youtube.com/embed/R5OPaVzrPao?feature=oembed&enablejsapi=1",
  ];

  // useEffect(() => {
  //   if (ytSliderRef.current) {
  //     setHeight((ytSliderRef.current.offsetWidth * 9) / 16);
  //   }
  // }, [ytSliderRef.current]);

  const handleMembershipCancel = useCallback(
    (reasons) => {
      const userId = user.id;

      cancelEventMembership(userId, membership?.id, reasons)
        .promise.then(() => {
          setHasMembershipBeenCancelled(true);
        })
        .catch((err) => {
          if (err) {
            openInfoModal(err.message);
          }
        })
        .finally(() => {
          closeCancelSubscriptionModal();
        });
    },
    [user, membership, cancelEventMembership]
  );

  const handleMembershipPause = useCallback(() => {
    const userId = user.id;

    if (membership?.metadata.isPaused) {
      unpauseEventMembership(userId)
        .catch((err) => {
          if (err) {
            openInfoModal(err.message);
          }
        })
        .finally(() => {
          closePauseSubscriptionModal();
        });
    } else {
      pauseEventMembership(userId)
        .catch((err) => {
          if (err) {
            openInfoModal(err.message);
          }
        })
        .finally(() => {
          closePauseSubscriptionModal();
        });
    }
  }, [user, membership]);

  const handlePageLeave = useCallback(() => {
    setPromoCode("");
    clearUpcomingEventsLoaded();
    clearMembershipItemsLoaded();
  }, []);

  const handleLocationClick = useCallback(
    () => {
      openCityPickerModal();
    },
    [
      /*openLocationSelectorModal*/
    ]
  );

  const handleCityChange = useCallback(
    (newCity) => {
      if (selectedCity === newCity) {
        return;
      }
      setSelectedCity(newCity);
      // loadUpcomingEvents(
      //   user?.id,
      //   "UPCOMING,STARTED,UPCOMING_FULL",
      //   { sort: "start", page: 0, size: 8 },
      //   user?.region || region,
      //   CITY_MAPPING[newCity]
      // );
    },
    [user, region, selectedCity]
  );

  const formatCurrency = useCurrencyFormat(
    user ? user.region : region //process.env.REACT_APP_REGION
  );

  const membershipSubscriptionItem = getMembershipSubscriptionItem(
    membershipItems?.data
  );

  const membershipItem = getSingleTicketItem(membershipItems?.data);

  const membershipPrice = membershipItem
    ? formatCurrency(membershipItem.price.amount)
    : null;

  const membershipPriceFormatted = membershipSubscriptionItem
    ? formatCurrency(membershipSubscriptionItem.price.amount)
    : null;

  const showSpecialOffer = !membership;

  const futureEvents = upcomingEvents;

  return (
    <>
      <ScrollToTopOnMount />
      <Title>TruBe: Unique Locations, Unforgettable Fitness Events</Title>
      <Meta
        name="description"
        content="TruBe: Unique Locations, Unforgettable Fitness classes in Pilates, Yoga, HIIT, Cardio, and more. Providing the best coaches and fitness and wellbeing experience."
      />
      <LinkMeta rel="canonical" href="https://trubeapp.com/lifestyle" />
      <Header
        showRegionSelect
        // behaviour="none"
        isFixed
        isLoggedIn={isUserLoggedIn}
        returnTo="/lifestyle"
        // region="US"
        heroRef={heroRef}
        onUpcomingClassesLinkClick={clearUpcomingEventsLoaded}
      />

      {/* Hero Section */}
      <div
        ref={heroRef}
        className="tbk-relative tbk-flex tbk-h-136 tbk-flex-col tbk-overflow-hidden tbk-pt-0 lg:tbk-min-h-[500px]"
      >
        <div className="tbk-absolute tbk-bottom-0 tbk-left-0 tbk-right-0 tbk-top-0 tbk-h-full tbk-w-full tbk-overflow-hidden">
          <video
            autoPlay
            loop
            className="tbk-absolute tbk-m-auto tbk-h-full tbk-w-full tbk-bg-cover tbk-bg-center tbk-object-cover"
            style={{
              backgroundImage:
                "url(https://assets-global.website-files.com/60785b43631c8625d8c10c7e/661d47f9e730db6130053f3f_Lifestyle-poster-00001.jpg)",
            }}
            muted
            playsInline
          >
            <source src="https://assets-global.website-files.com/60785b43631c8625d8c10c7e/661d47f9e730db6130053f3f_Lifestyle-transcode.mp4" />
            <source src="https://assets-global.website-files.com/60785b43631c8625d8c10c7e/661d47f9e730db6130053f3f_Lifestyle-transcode.webm" />
          </video>
          <div className="tbk-absolute tbk-h-full tbk-w-full tbk-bg-gradient-to-r tbk-from-basic-trueblack/60 tbk-to-basic-trueblack/60" />
        </div>
        <div className="tbk-absolute tbk-bottom-0 tbk-left-0 tbk-right-0 tbk-top-0 tbk-bg-[#C3A48D]/20" />

        <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-8">
          <div className="tbk-mb-7 tbk-mt-2 tbk-flex tbk-h-full tbk-grow tbk-flex-col tbk-items-center tbk-justify-center lg:tbk-mt-7">
            <div className="tbk-mb-0.5 tbk-flex tbk-flex-col tbk-items-center tbk-justify-center md:tbk-justify-start">
              <TruBe className="" />
              <img
                className="tbk-h-9"
                src={lifestyleCollectiveLogo}
                srcSet={`${lifestyleCollectiveLogo} 1x, ${lifestyleCollectiveLogo2x} 2x, ${lifestyleCollectiveLogo3x} 3x`}
                alt="Lifestyle Collective"
              />
            </div>

            <p className="tbk-text-main tbk-mb-2 tbk-text-basic-white lg:tbk-mb-2">
              Elevated Fitness Classes
            </p>

            {showSpecialOffer ? (
              <div className="tbk-mb-4 tbk-flex tbk-grow lg:tbk-mb-4">
                <div className="tbk-mx-3 tbk-w-full sm:tbk-mx-auto">
                  <SpecialOfferBox
                    isUserLoggedIn={isUserLoggedIn}
                    stateFrom={location.pathname}
                    priceLabel={membershipPriceFormatted}
                    region={user ? user.region : region}
                    city={user && user.city ? user.city : selectedCity}
                    //onCtaClick={clearUpcomingEventsLoaded}
                  />
                </div>
              </div>
            ) : null}

            {false && !membership && !showSpecialOffer ? (
              <Link
                to={{
                  pathname: isUserLoggedIn ? `/checkout` : `/become-a-member`,
                  search: isUserLoggedIn
                    ? `?membershipId=1&region=${
                        user ? user.region : region
                      }&city=${user && user.city ? user.city : selectedCity}`
                    : `?return_to=${encodeURIComponent(
                        getBuyMembershipPath({
                          region: user ? user.region : region,
                          city: user && user.city ? user.city : selectedCity,
                        })
                      )}`,
                  state: { from: location.pathname },
                }}
                // href="https://trubeapp.com/app-redirects/deeplink-redirect-events.html"
                // target="_blank"
              >
                <Button
                  variant={"secondary"}
                  className="tbk-mb-3 tbk-mt-3 tbk-inline-flex tbk-w-80 tbk-pl-5 tbk-pr-5 lg:tbk-mb-0 lg:tbk-mt-5"
                  endIcon={<ArrowRightIcon className="tbk-ml-0.5" />}
                >
                  Join Now
                </Button>
              </Link>
            ) : null}
          </div>
        </div>
      </div>

      {membership ? (
        <div className="tbk-flex">
          <div className="tbk-mx-3 tbk--mt-32 tbk-w-132 tbk-min-w-0 sm:tbk-mx-auto">
            <MembershipBox
              membershipInfo={membership}
              symbol="$"
              region={
                user ? user.region : region /*process.env.REACT_APP_REGION*/
              }
              onCtaClick={() => setIsManageMembershipModalOpen(true)}
            />
          </div>
        </div>
      ) : null}

      {!membership ? (
        <div className="tbk-pt-7 md:tbk-pt-8">
          {/* Benefits Section */}
          <div className="tbk-container tbk-mx-auto tbk-px-2 lg:tbk-px-8">
            <HeadlineWithSeparator
              component="h3"
              titleClass="tbk-bg-basic-white tbk-text-blue-grey tbk-text-h3-subtitle_ tbk-text-title-bold lg:tbk-text-title-bold !tbk-font-semibold"
              className="tbk-mb-3 lg:tbk-mb-6"
              separatorClass={"tbk--mt-1.5 lg:tbk--mt-2"}
            >
              The Experience
            </HeadlineWithSeparator>
          </div>

          <div className="tbk-mb-4 tbk-flex tbk-justify-center">
            <div className="tbk-max-w-full tbk-overflow-hidden tbk-px-2 lg:tbk-px-2">
              <div className="sm:tb-slider tbk-flex tbk-flex-col !tbk-flex-nowrap tbk-gap-2 sm:tbk-flex-row lg:tbk-grid-cols-4 lg:tbk-gap-2 xl:tbk-grid">
                <BenefitCard
                  imgs={[benefitsExclusiveVenues, benefitsExclusiveVenues2x]}
                  title={"Exclusive Venues"}
                  text="Step into world-class hotels and members clubs reimagined as your wellness Studio."
                />
                <BenefitCard
                  imgs={[
                    benefitsClassesByTopCoaches,
                    benefitsClassesByTopCoaches2x,
                  ]}
                  title="World Class Coaches"
                  text="Unlock your potential with guidance from world-class coaches who elevate every session."
                />
                <BenefitCard
                  imgs={[
                    benefitsBrandCollabsGifts,
                    benefitsBrandCollabsGifts2x,
                  ]}
                  title={
                    <span>
                      Innovative Brand
                      <br />
                      Collaborations
                    </span>
                  }
                  text="Experience the latest in wellness with exclusive gifts and collaborations from the industry's most innovative brands."
                />
                <BenefitCard
                  imgs={[
                    benefitsPostWorkoutBrandActivations,
                    benefitsPostWorkoutBrandActivations2x,
                  ]}
                  title={
                    <span>
                      Cutting-Edge
                      <br />
                      Equipment & Tools
                    </span>
                  }
                  text="Enjoy access to cutting-edge wellness tools, equipment and recovery machines designed to boost your fitness and longevity."
                />
              </div>
            </div>
          </div>

          <div className="tbk-relative tbk-pb-7 tbk-text-center lg:tbk-pb-7">
            <Link
              to={{
                pathname: isUserLoggedIn ? `/checkout` : `/become-a-member`,
                search: isUserLoggedIn
                  ? `?membershipId=1&region=${
                      user ? user.region : region
                    }&city=${user?.city || city}`
                  : `?return_to=${encodeURIComponent(
                      getBuyMembershipPath({
                        region: user ? user.region : region,
                        city: user?.city || city,
                      })
                    )}`,
                state: { from: location.pathname },
              }}
              className="tbk-inline-block"
            >
              <Button
                variant={"secondary"}
                className="tbk-w-64"
                endIcon={<ArrowRightIcon className="tbk-ml-0.5" />}
              >
                Explore All Benefits
              </Button>
            </Link>
          </div>
        </div>
      ) : null}

      {/* Pricing Section */}
      {!membership ? (
        <div className="tbk-container tbk-mx-auto tbk-overflow-hidden tbk-px-2 tbk-pb-2 tbk-pt-4 lg:tbk-px-8 lg:tbk-pb-6 lg:tbk-pt-9">
          <TitleWithSeparator
            className="tbk-mb-4"
            titleClass="tbk-text-center tbk-bg-basic-white tbk-text-blue-grey tbk-text-title-bold"
            separatorClass="tbk-border-blue-grey-light"
          >
            Pricing
          </TitleWithSeparator>
          <div className="tb-slider tbk-flex-nowrap tbk-gap-1 lg:tbk-grid lg:tbk-grid-cols-2 lg:tbk-gap-2 lg:tbk-gap-4">
            <div
              className="tbk-relative tbk-flex tbk-w-70 tbk-shrink-0 tbk-grow md:tbk-w-auto"
              //onClick={onClick}
            >
              <div className="tbk-absolute tbk-left-0 tbk-top-0 tbk-h-3 tbk-w-full tbk-rounded-t-2xl tbk-bg-pricing-card-top-border-gradient" />
              <div
                className="tbk-relative tbk-mb-2 tbk-mt-1 tbk-flex tbk-max-w-full tbk-flex-col tbk-overflow-hidden tbk-rounded-2xl tbk-bg-basic-white tbk-p-4 tbk-shadow-trube"
                //onClick={onClick}
              >
                <div className="tbk-text-h2-black tbk-mb-1 tbk-text-primary">
                  Membership
                </div>
                <div className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  {membershipPriceFormatted || (
                    <span className="tbk-inline-block tbk-w-2 after:tbk-animate-dots after:tbk-content-['.']" />
                  )}{" "}
                  per month
                </div>
                <div className="tbk-text-secondary lg:tbk-text-h3-subtitle tbk-mb-1 tbk-text-primary">
                  <span className="tbk-text-green">3 classes</span> per month
                </div>
                {/*<div className="tbk-text-secondary lg:tbk-text-h3-subtitle tbk-mb-1 tbk-text-blue-grey">
                  3 classes per month thereafter
                </div>*/}

                <hr />
                <ul className="tbk-mb-0 tbk-min-w-0 tbk-grow tbk-list-disc tbk-text-left sm:tbk-pl-3 md:tbk-pl-3">
                  <li className="tbk-text-small lg:tbk-text-main tbk-mb-1 tbk-text-primary">
                    Access to member only events
                  </li>
                  <li className="tbk-text-small lg:tbk-text-main tbk-mb-1 tbk-text-primary">
                    Priority RSVP to all classes
                  </li>
                  <li className="tbk-text-small lg:tbk-text-main tbk-mb-1 tbk-text-primary">
                    1 Complimentary guest pass
                  </li>
                  <li className="tbk-text-small lg:tbk-text-main tbk-mb-1 tbk-text-primary">
                    Welcome kit including; a tote bag & water bottle (applicable
                    after your second month)
                  </li>
                  <li className="tbk-text-small lg:tbk-text-main tbk-mb-1 tbk-text-primary">
                    Exclusive brand deals through our member marketplace
                  </li>
                  <li className="tbk-text-small lg:tbk-text-main tbk-mb-1 tbk-text-primary">
                    15% off private 1:1 training sessions
                  </li>
                </ul>
                <hr />
                <Link
                  to={{
                    pathname: isUserLoggedIn ? `/checkout` : `/become-a-member`,
                    search: isUserLoggedIn
                      ? `?membershipId=1&region=${
                          user ? user.region : region
                        }&city=${user && user.city ? user.city : selectedCity}`
                      : `?return_to=${encodeURIComponent(
                          getBuyMembershipPath({
                            region: user ? user.region : region,
                            city: user && user.city ? user.city : selectedCity,
                          })
                        )}`,
                    state: { from: location.pathname },
                  }}
                  // href="https://trubeapp.com/app-redirects/deeplink-redirect-events.html"
                  // target="_blank"
                >
                  <Button
                    size="md"
                    className="tbk-w-full"
                    endIcon={<ArrowRightIcon />}
                  >
                    Join Today
                  </Button>
                </Link>
              </div>
            </div>

            <div
              className="tbk-relative tbk-flex tbk-w-70 tbk-shrink-0 tbk-grow md:tbk-w-auto"
              //onClick={onClick}
            >
              <div className="tbk-absolute tbk-left-0 tbk-top-0 tbk-h-3 tbk-w-full tbk-rounded-t-2xl tbk-bg-pricing-card-top-border-gradient" />
              <div
                className="tbk-relative tbk-mb-2 tbk-mt-1 tbk-flex tbk-max-w-full tbk-flex-col tbk-overflow-hidden tbk-rounded-2xl tbk-bg-basic-white tbk-p-4 tbk-shadow-trube"
                //onClick={onClick}
              >
                <div className="tbk-text-h2-black tbk-mb-1 tbk-whitespace-nowrap tbk-text-primary">
                  Pay Per Class
                </div>
                <div className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-mb-1 tbk-text-primary">
                  {membershipPrice || (
                    <span className="tbk-inline-block tbk-w-2 after:tbk-animate-dots after:tbk-content-['.']" />
                  )}{" "}
                  per class
                </div>
                <div className="tbk-text-secondary lg:tbk-text-h3-subtitle tbk-mb-1 tbk-text-blue-grey">
                  Choose any class you like and buy a single ticket as you go.
                </div>

                <hr />
                <ul className="tbk-mb-0 tbk-min-w-0 tbk-grow tbk-list-disc tbk-text-left sm:tbk-pl-3 md:tbk-pl-3">
                  {/*<li className="tbk-text-small lg:tbk-text-main tbk-mb-1 tbk-text-primary">
                    Flexibility and ease – Try a TruBe class on your terms.
                  </li>*/}
                  <li className="tbk-text-small lg:tbk-text-main tbk-mb-1 tbk-text-primary">
                    No commitment - Join us when it suits you, no strings
                    attached
                  </li>
                </ul>
                <hr />
                <Link to={`/events/upcoming`}>
                  <Button
                    size="md"
                    className="tbk-w-full"
                    endIcon={<ArrowRightIcon />}
                    onClick={handlePageLeave}
                  >
                    View Classes
                  </Button>
                </Link>
              </div>
              {/*<PricingCard />*/}
            </div>
          </div>
        </div>
      ) : null}

      {/* Community Section */}
      {!membership ? (
        <div
          // ref={firstElementRef}
          className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-4 tbk-pt-4 lg:tbk-pb-22 lg:tbk-pt-22"
        >
          <div className="tbk-container tbk-px-2 lg:tbk-px-8">
            <div className="tbk-flex tbk-justify-center">
              <div className="tbk-w-156 lg:tbk-mb-0">
                <h2 className="tbk-text-title-bold lg:tbk-text-h2-black tbk-mb-2 !tbk-leading-tight tbk-text-primary lg:tbk-mb-4 lg:tbk-text-center lg:!tbk-leading-tight">
                  Connect, Inspire, Flourish: Join TruBe's Wellness Community
                </h2>
                <p className="tbk-text-main tbk-mb-4 tbk-text-primary lg:tbk-text-center">
                  At TruBe, wellness goes beyond the workout. It’s about
                  building connections, creating community, and nurturing the
                  mind and body through curated fitness and wellness
                  experiences. Weekly sessions in HIIT, Boxing, Pilates, Yoga,
                  and more are led by expert coaches at exclusive venues,
                  providing space to focus on your well-being. Pre- and
                  post-activations offer moments to engage and connect with
                  others.
                </p>
                <p className="tbk-text-h3-subtitle tbk-mb-7_ tbk-text-blue-grey lg:tbk-text-center">
                  TruBe’s story is strengthened by partnerships with top brands
                  in fitness, wellness, nutrition, and fashion. These
                  collaborations enhance the experience, offering unique
                  activations that deepen connections and inspire a more
                  balanced, intentional life.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Explore Workouts Section */}
      {!membership ? (
        <>
          <div className="tbk-container tbk-mx-auto tbk-px-2 tbk-pt-4 lg:tbk-px-8 lg:tbk-pt-7">
            <TitleWithSeparator
              className="tbk-mb-3"
              titleClass="tbk-text-center tbk-bg-basic-white tbk-text-blue-grey tbk-text-h3-subtitle lg:tbk-text-title-bold"
              separatorClass="tbk-border-blue-grey-light"
            >
              Explore Workouts
              <br className="md:tbk-hidden" /> & Meditations
            </TitleWithSeparator>
          </div>

          <div className="big-container tbk-mx-auto tbk-mb-4 tbk-flex tbk-justify-center">
            <div className="tbk-w-full tbk-max-w-full tbk-overflow-hidden tbk-px-2 lg:tbk-px-2">
              <div className="tb-slider tbk-flex-nowrap tbk-gap-2 lg:tbk-grid lg:tbk-grid-cols-4 lg:tbk-gap-2">
                <ExploreCard
                  imgs={[exploreCardImg1, exploreCardImg12x]}
                  title={"Pilates"}
                />
                <ExploreCard
                  imgs={[exploreCardImg2, exploreCardImg22x]}
                  title={"Sound Healing & Meditation"}
                />
                <ExploreCard
                  imgs={[exploreCardImg3, exploreCardImg32x]}
                  title={"Strength & Mobility"}
                />
                <ExploreCard
                  imgs={[exploreCardImg4, exploreCardImg42x]}
                  title={"Yoga"}
                />
              </div>
            </div>
          </div>

          <div className="tbk-relative tbk-pb-4 tbk-text-center lg:tbk-pb-7">
            <Link to={`/events/upcoming`} className="tbk-inline-block">
              <Button
                variant="secondary"
                className="tbk-w-64"
                endIcon={<ArrowRightIcon />}
                onClick={handlePageLeave}
              >
                Explore All Classes
              </Button>
            </Link>
          </div>
        </>
      ) : null}

      {/* Series of Classes Section */}
      {series.length > 0 ? (
        <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-4 tbk-pt-4 lg:tbk-pb-7 lg:tbk-pt-9">
          <div className="tbk-container tbk-px-3 lg:tbk-px-8">
            <HeadlineWithSeparator
              component="h3"
              titleClass="tbk-bg-basic-white tbk-text-blue-grey tbk-text-h3-subtitle lg:tbk-text-title-bold"
              className="tbk-mb-3 lg:tbk-mb-5"
              separatorClass={"tbk--mt-1.5 lg:tbk--mt-2"}
            >
              Series of Classes
            </HeadlineWithSeparator>

            <CityPickerButton
              className="tbk-mb-4"
              city={selectedCity}
              onCityChange={handleCityChange}
            />

            <div className="tbk-mb-6 ">
              <SeriesOfClassesSlider
                showArrows={clientWidth() > theme.responsive.breakpoints.lg}
                className="tbk-pb-3"
                itemComponent={EventSeriesSlide}
                itemProps={{
                  to: !isUserLoggedIn && {
                    pathname: `/become-a-member`,
                    search: `?return_to=${encodeURIComponent(
                      getBuyMembershipPath({
                        region: user ? user.region : region,
                        city: user?.city || city,
                      })
                    )}`,
                    state: { from: location.pathname },
                  },
                  imgClassName: "tbk-h-48 lg:tbk-h-75",
                  showRsvpCta: true,
                  isUserLoggedIn: isUserLoggedIn,
                  onRsvpCtaClick: handlePageLeave,
                }}
                eventSeries={series}
                isFetching={isSeriesLoading}
              />
            </div>
          </div>
          <div className="tbk-mb-3_ lg:tbk-mb-5_ tbk-relative tbk-text-center">
            <Link to={`/events/series`} className="tbk-inline-block">
              <Button
                size={"md"}
                className="tbk-w-64"
                endIcon={<ArrowRightIcon />}
                onClick={handlePageLeave}
              >
                View All Upcoming
              </Button>
            </Link>
          </div>
        </div>
      ) : null}

      {/* Upcoming Classes Section */}
      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-4 tbk-pt-4 lg:tbk-pb-7 lg:tbk-pt-9">
        <div className="tbk-container tbk-px-3 lg:tbk-px-8">
          <HeadlineWithSeparator
            component="h3"
            titleClass="tbk-bg-basic-white tbk-text-blue-grey tbk-text-h3-subtitle lg:tbk-text-title-bold"
            className="tbk-mb-3 lg:tbk-mb-5"
            separatorClass={"tbk--mt-1.5 lg:tbk--mt-2"}
          >
            Upcoming Classes
          </HeadlineWithSeparator>

          <CityPickerButton
            className="tbk-mb-4"
            city={selectedCity}
            onCityChange={handleCityChange}
          />
          <div className="tbk-mb-6 ">
            <UpcomingExperiencesSlider
              showArrows={clientWidth() > theme.responsive.breakpoints.lg}
              className="tbk-pb-3"
              itemComponent={EventCard}
              itemProps={{
                to: !isUserLoggedIn && {
                  pathname: `/become-a-member`,
                  search: `?return_to=${encodeURIComponent(
                    getBuyMembershipPath({
                      region: user ? user.region : region,
                      city: user?.city || city,
                    })
                  )}`,
                  state: { from: location.pathname },
                },
                imgClassName: "tbk-h-48 lg:tbk-h-75",
                showRsvpCta: true,
                isUserLoggedIn: isUserLoggedIn,
                onRsvpCtaClick: handlePageLeave,
              }}
              futureEvents={futureEvents}
              isFetching={isUpcomingEventsLoading}
            />
          </div>
        </div>
        <div className="tbk-mb-3_ lg:tbk-mb-5_ tbk-relative tbk-text-center">
          <Link to={`/events/upcoming`} className="tbk-inline-block">
            <Button
              size={"md"}
              className="tbk-w-64"
              endIcon={<ArrowRightIcon />}
              onClick={handlePageLeave}
            >
              View All Upcoming
            </Button>
          </Link>
        </div>
      </div>

      {!membership ? (
        <section className="tbk-mb-0 tbk-pb-7 tbk-pt-7 lg:tbk-pb-7 lg:tbk-pt-9">
          <div className="tbk-container tbk-mx-auto tbk-px-3 lg:tbk-px-8">
            <HeadlineWithSeparator
              component="h3"
              titleClass="tbk-bg-basic-white tbk-text-blue-grey tbk-text-h3-subtitle"
              className="tbk-mb-5 lg:tbk-mb-6"
              separatorClass={"tbk--mt-1.5 lg:tbk--mt-2"}
            >
              The Venues
            </HeadlineWithSeparator>
            {/*<div className="tbk-relative tbk-mb-5 tbk-text-center lg:tbk-mb-6">*/}
            {/*  <h3 className="tbk-text-h3-subtitle lg:tbk-text-title-bold tbk-inline-block tbk-bg-basic-white tbk-px-2 tbk-text-center tbk-text-blue-grey">*/}
            {/*    The Venues*/}
            {/*  </h3>*/}
            {/*  <hr className="tbk-mb-0 tbk--mt-1.5 tbk-border-blue-grey-light lg:tbk--mt-2" />*/}
            {/*</div>*/}
          </div>

          <LogoBar />

          <h3 className="tbk-text-h3-subtitle tbk-text-center tbk-text-blue-grey">
            And Many Others...
          </h3>
        </section>
      ) : null}
      {/* Elevated Classes Section */}
      {false && elevatedEvents.length > 0 ? (
        <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pb-4 tbk-pt-4 lg:tbk-pb-7 lg:tbk-pt-9">
          <div className="tbk-container tbk-px-3 lg:tbk-px-8">
            <KeyIcon className="tbk-mx-auto tbk-mb-2" />
            <HeadlineWithSeparator
              component="h3"
              titleClass="tbk-bg-basic-white tbk-text-blue-grey tbk-text-h3-subtitle lg:tbk-text-title-bold"
              className="tbk-mb-3 lg:tbk-mb-5"
              separatorClass={"tbk--mt-1.5 lg:tbk--mt-2"}
            >
              Elevated Classes
            </HeadlineWithSeparator>

            <div className="tbk-flex tbk-justify-center tbk-text-center">
              <div className="lg:tbk-w-[420px]">
                <p className="tbk-text-main tbk-mb-4 tbk-text-blue-grey lg:tbk-mb-4">
                  Certain TBe events are invitation-only, offering a more
                  intimate and immersive wellness experience.
                </p>
              </div>
            </div>

            <div className="tbk-mb-6 ">
              <ElevatedClassesSlider
                showArrows={clientWidth() > theme.responsive.breakpoints.lg}
                className="tbk-pb-3"
                itemComponent={EventCard}
                itemProps={{
                  to: !isUserLoggedIn && {
                    pathname: `/become-a-member`,
                    search: `?return_to=${encodeURIComponent(
                      getBuyMembershipPath({
                        region: user ? user.region : region,
                        city: user?.city || city,
                      })
                    )}`,
                    state: { from: location.pathname },
                  },
                  imgClassName: "tbk-h-48 lg:tbk-h-75",
                  showRsvpCta: true,
                  isUserLoggedIn: isUserLoggedIn,
                  onRsvpCtaClick: handlePageLeave,
                }}
                gatedEvents={elevatedEvents}
                isFetching={isElevatedEventsLoading}
              />
            </div>
          </div>
          <div className="tbk-mb-3_ lg:tbk-mb-5_ tbk-relative tbk-text-center">
            <Link to={`/events/elevated`} className="tbk-inline-block">
              <Button
                size={"md"}
                className="tbk-w-64"
                endIcon={<ArrowRightIcon />}
                onClick={handlePageLeave}
              >
                All Elevated Classes
              </Button>
            </Link>
          </div>
        </div>
      ) : null}

      {/* Previous Classes Section */}
      {false &&
      ((membership && completedEvents.length > 0) ||
        isCompletedEventsLoading) ? (
        <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-overflow-hidden tbk-pb-4 tbk-pt-3 lg:tbk-pb-7 lg:tbk-pt-9">
          <div className="tbk-container tbk-px-3 lg:tbk-px-8">
            <HeadlineWithSeparator
              component="h3"
              titleClass="tbk-bg-basic-white tbk-text-blue-grey tbk-text-h3-subtitle lg:tbk-text-title-bold"
              className="tbk-mb-6 tbk-mt-4 lg:tbk-mt-0"
              separatorClass={"tbk--mt-1.5 lg:tbk--mt-2"}
            >
              Previous Classes
            </HeadlineWithSeparator>

            <div className="tb-slider tb-past-events-slider tbk-flex-nowrap tbk-gap-2 md:tbk-grid-cols-2 lg:tbk-grid lg:tbk-grid-cols-4">
              {completedEvents.length > 0 && !isCompletedEventsLoading
                ? completedEvents.map((evt) => (
                    <Link
                      key={evt.id}
                      to={`/events/${evt.id}`}
                      // onClick={clearCompletedEventsLoaded}
                    >
                      <div className="tb-slide tbk-mb-5 tbk-grow tbk-basis-72 lg:tbk-mb-0">
                        <div className="tbk-relative tbk-overflow-hidden tbk-rounded-2xl">
                          <img
                            className="tbk-h-52 tbk-w-72 tbk-w-full tbk-min-w-[208px] tbk-object-cover lg:tbk-h-66 xl:tbk-min-w-[268px]"
                            src={evt.pastPhoto}
                            alt="Access"
                          />
                        </div>
                      </div>
                    </Link>
                  ))
                : isCompletedEventsLoading &&
                  [...new Array(4)].map((v, index) => (
                    <Skeleton
                      key={index}
                      className="tbk-w-72_ tbk-relative tbk-h-52 tbk-w-full tbk-min-w-[208px] tbk-rounded-2xl tbk-bg-blue-grey-light lg:tbk-h-66 xl:tbk-min-w-[268px]"
                    >
                      <TruBeLogo className="tbk-absolute tbk-left-1/2 tbk-top-1/2 tbk--translate-x-1/2 tbk--translate-y-1/2" />
                    </Skeleton>
                  ))}
            </div>
            <div className="tbk-relative tbk-mt-4 tbk-text-center lg:tbk-mt-4">
              <Link to={`/events/previous`} className="tbk-inline-block">
                <Button
                  size={"md"}
                  className="tbk-w-64"
                  endIcon={<ArrowRightIcon />}
                  // onClick={clearCompletedEventsLoaded}
                >
                  View All Previous
                </Button>
              </Link>
            </div>
          </div>
        </div>
      ) : null}

      {/* Offers Section */}
      {false && (offers.length > 0 || isOffersLoading) ? (
        <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-overflow-hidden tbk-pb-4 tbk-pt-4 lg:tbk-pb-7 lg:tbk-pt-7">
          <div className="tbk-container tbk-px-3 lg:tbk-px-8">
            <TitleWithSeparator
              className="tbk-mb-3"
              titleClass="tbk-text-center tbk-bg-basic-white tbk-text-blue-grey tbk-text-h3-subtitle lg:tbk-text-title-bold"
              separatorClass="tbk-border-blue-grey-light"
            >
              Exclusive Member Discounts
              <br className="md:tbk-hidden" /> on Top Brands
            </TitleWithSeparator>

            <OffersSlider
              className="tbk-pb-3"
              showArrows={clientWidth() > theme.responsive.breakpoints.lg}
              autoPlay={autoPlay}
              offers={offers}
              isFetching={isOffersLoading}
            />
          </div>
        </div>
      ) : null}

      {/* FAQ Section */}
      <div
        id="faq"
        ref={questionsSectionRef}
        className="tbk-relative tbk-bg-basic-white tbk-pb-7 tbk-pt-7 lg:tbk-pb-0 lg:tbk-pt-9"
      >
        <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-8 lg:tbk-pb-9">
          <TitleWithSeparator
            className="tbk-mb-4"
            titleClass="tbk-text-center tbk-text-blue-grey tbk-text-h3-subtitle lg:tbk-text-title-bold"
            separatorClass="tbk-border-blue-grey-light"
          >
            Any Questions?
          </TitleWithSeparator>

          <div className="tbk-flex tbk-flex-col-reverse md:tbk-grid md:tbk-grid-cols-homepage-two-cols-layout md:tbk-items-start md:tbk-gap-8">
            <div className="tbk-flex tbk-flex-col tbk-items-center tbk-rounded-2xl tbk-bg-grey-light tbk-p-3">
              <img src={askConcierge} alt="Ask TruBe Concierge" />
              <p className="tbk-text-epic-medium tbk-my-4 tbk-text-center !tbk-leading-none tbk-text-primary">
                We're here
                <br /> to <span className="tbk-text-coral">help</span>
              </p>
              <Button
                size="md"
                className="tbk-w-68 lg:tbk-w-full"
                endIcon={<ArrowRightIcon />}
                onClick={() => setIsAskConciergeModalOpen(true)}
              >
                Ask TruBe Concierge
              </Button>
            </div>

            <div className="tbk-mb-5 tbk-text-center lg:tbk-mb-0">
              <AccordionItem
                className=""
                title={"What is the TruBe Lifestyle membership?"}
                text={
                  <p>
                    The TruBe Lifestyle membership is a subscription-based
                    service that grants you access to curated wellness classes
                    designed to enhance your overall well-being. For a monthly
                    fee of{" "}
                    {membershipPriceFormatted || (
                      <span className="tbk-inline-block tbk-w-2 after:tbk-animate-dots after:tbk-content-['.']" />
                    )}
                    , you can attend up to 3 classes per month, each focusing on
                    various TruBe offerings such as HIIT, Mobility, Pilates,
                    Yoga, Meditation, Sound Healing, and more.
                  </p>
                }
              />
              <AccordionItem
                className="tbk-mt-3"
                title={"What can I expect from TruBe Lifestyle classes?"}
                text={
                  "TruBe Lifestyle classes present an array of activities guided by leading coaches spanning various wellness modalities. Whether you seek an invigorating HIIT session or serene meditation and sound healing, our classes accommodate all preferences and fitness levels. Held at exclusive venues and led by top TruBe coaches, each class includes pre and post-session brand classes and curated TruBe swag bags."
                }
              />
              <AccordionItem
                className="tbk-mt-3"
                title={"How do I sign up for the TruBe Lifestyle membership?"}
                text={
                  "Signing up for the TruBe Lifestyle membership is easy! Simply download the TruBe app, click on the TruBe lifestyle option, and choose the TruBe subscription membership plan. Once subscribed, you'll gain instant access to our complete class calendar and can start RSVPing for your preferred classes right away."
                }
              />
              <AccordionItem
                className="tbk-mt-3"
                title={
                  "Can I attend classes at exclusive venues with the TruBe Lifestyle membership?"
                }
                text={
                  "Absolutely! TruBe Lifestyle classes are held at exclusive venues to provide you with a premium wellness experience. From chic studios to tranquil outdoor settings, each venue is carefully selected to elevate your overall event class."
                }
              />
              {showMoreQuestions ? (
                <AccordionItem
                  className="tbk-mt-3"
                  title={"How do I RSVP for TruBe Lifestyle classes?"}
                  text={
                    "To RSVP for TruBe Lifestyle classes, simply browse through our complete class calendar on the TruBe app and select the classes you wish to attend. Once you've made your selections, click on the RSVP button, and your ticket(s) will be automatically added to your Apple Wallet upon confirmation. All classes are subject to availability."
                  }
                />
              ) : null}
              {showMoreQuestions ? (
                <AccordionItem
                  className="tbk-mt-3"
                  title={"What if I can't attend all 3 classes in a month?"}
                  text={
                    "We understand that life can get busy, and you may not always be able to attend all 3 classes in a month. That's why your TruBe Lifestyle membership allows you the flexibility to attend up to 3 classes per month, giving you the freedom to prioritize your well-being at your own pace."
                  }
                />
              ) : null}
              {showMoreQuestions ? (
                <AccordionItem
                  className="tbk-mt-3"
                  title={
                    "How can I connect with other members of the TruBe community?"
                  }
                  text={
                    "The TruBe Lifestyle membership not only offers you access to curated wellness classes but also provides you with an opportunity to connect with like-minded individuals who share your passion for health and wellness. Whether it's striking up a conversation with fellow attendees at a class or joining our online community forums, there are plenty of ways to forge meaningful connections within the TruBe community."
                  }
                />
              ) : null}
              {showMoreQuestions ? (
                <AccordionItem
                  className="tbk-mt-3"
                  title={"How can I cancel my TruBe Lifestyle membership?"}
                  text={
                    "To cancel your TruBe Lifestyle membership, simply log in to your TruBe account on the app or website and navigate to the subscription settings. From there, you'll find the option to cancel your membership."
                  }
                />
              ) : null}

              {showMoreQuestions ? (
                <AccordionItem
                  className="tbk-mt-3"
                  title={
                    "What happens if I don’t cancel my RSVP 12 hours before a class?"
                  }
                  text={
                    "If you cancel your RSVP less than 12 hours before a class or fail to show up, you will be charged a $10 penalty."
                  }
                />
              ) : null}

              {/*{showMoreQuestions ? (*/}
              {/*  <AccordionItem*/}
              {/*    className="tbk-mt-3"*/}
              {/*    title={"Questions?"}*/}
              {/*    text={*/}
              {/*      <p>*/}
              {/*        Email us anytime at{" "}*/}
              {/*        <a href="mailto:members@trubeapp.com">*/}
              {/*          members@trubeapp.com*/}
              {/*        </a>*/}
              {/*        . We’re here to help!*/}
              {/*      </p>*/}
              {/*    }*/}
              {/*  />*/}
              {/*) : null}*/}

              {!showMoreQuestions ? (
                <button
                  className="tbk-text-main tbk-mb-1 tbk-mt-1 tbk-inline-flex tbk-items-center tbk-text-blue-grey tbk-underline lg:tbk-mb-0"
                  onClick={handleMoreQuestionsClick}
                >
                  <div className="">More Questions</div>
                  <CaretDownIcon />
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {!membership ? <TruBeInPress /> : null}

      <AppBanner />
      <Footer />

      <AskConciergeModal
        isOpen={isAskConciergeModalOpen}
        onClose={() => setIsAskConciergeModalOpen(false)}
      />

      <ManageMembershipModal
        isOpen={isManageMembershipModalOpen}
        showCta={!hasMembershipBeenCancelled}
        membershipInfo={membership}
        region={user ? user.region : region /*process.env.REACT_APP_REGION*/}
        onCancelMembershipClick={openCancelSubscriptionModal}
        onPauseMembershipClick={openPauseSubscriptionModal}
        onClose={() => setIsManageMembershipModalOpen(false)}
      />

      <Modal
        isOpen={isGalleryCarouselModalOpen}
        // onAfterOpen={this.afterOpenModal}
        onRequestClose={closeGalleryCarouselModal.bind(null, true)}
        style={modalStyles.galleryCarouselDefault}
        contentLabel="Gallery Carousel"
      >
        {/*<Link role='button' onClick={this.closeDateTimeSelectorModal}><Icon name='close' className='tb-pd-0' width='32px' height='32px' /></Link>*/}
        <div
          role="button"
          className="tbk-absolute tbk-right-2 tbk-top-2 tbk-z-10 tbk-p-1"
          onClick={closeGalleryCarouselModal}
        >
          <CloseIcon className="tbk-p-0 tbk-text-basic-white" />
        </div>
        <div className="modal-gallery-carousel u-d-flex_ u-flex-column_ tbk-h-full">
          <Carousel
            // images={images}
            hasIndexBoard={false}
            hasMediaButton={false}
            hasSizeButton={false}
            hasThumbnails={false}
            index={galleryInitialSlide}
            leftIcon={
              <svg
                width="8"
                height="13"
                viewBox="0 0 8 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8 1.5L6.5 0L0 6.5L6.5 13L8 11.5L3 6.5L8 1.5Z"
                  fill="#CBCFD5"
                />
              </svg>
            }
            rightIcon={
              <svg
                width="8"
                height="13"
                viewBox="0 0 8 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0 1.5L1.5 0L8 6.5L1.5 13L0 11.5L5 6.5L0 1.5Z"
                  fill="#CBCFD5"
                />
              </svg>
            }
            style={{ height: "100%", width: "100%" }}
            // onIndexChange={({ curIndex, curIndexForDisplay }) => {
            //   const ytPlayerIframes = document.querySelectorAll(".yt_player_iframe");
            // }}
          >
            {galleryImages &&
              galleryImages.map((url) => (
                <figure className="_3iVQ0">
                  <img
                    className="_t1897 tbk-h-auto_ tbk-top-1/2 tbk-mx-auto tbk-w-auto tbk--translate-y-1/2 tbk-object-contain"
                    src={url}
                  />
                </figure>
              ))}
          </Carousel>
        </div>
      </Modal>
      <CancelMembershipModal
        open={isCancelSubscriptionModalOpen}
        isTrial={membership?.metadata.status === "TRIALING"}
        region={user ? user.region : region /*process.env.REACT_APP_REGION*/}
        onClose={closeCancelSubscriptionModal}
        onMembershipCancel={handleMembershipCancel}
      />
      <PauseMembershipModal
        open={isPauseSubscriptionModalOpen}
        currentPeriodEnd={membership?.metadata.currentPeriodEnd}
        resumeAt={membership?.metadata.resumeAt}
        paused={membership?.metadata.isPaused}
        region={user ? user.region : region /*process.env.REACT_APP_REGION*/}
        onClose={closePauseSubscriptionModal}
        onMembershipPause={handleMembershipPause}
      />

      <EventRatingModal onClose={() => {}} />

      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isInfoModalOpen}
        // onAfterOpen={afterOpenInfoModal}
        onRequestClose={closeInfoModal}
        style={
          clientWidth() < theme.responsive.breakpoints.sm
            ? modalStyles.infoMobile
            : infoModalTitle === "Referral Bonus Applied" ||
              infoModalTitle !== "Ambassador Link Activated"
            ? Object.assign(
                {},
                { ...modalStyles.info },
                {
                  content: {
                    ...modalStyles.info.content,
                    width: "320px",
                  },
                }
              )
            : modalStyles.info
        }
        contentLabel="Info Modal"
      >
        <div className="modal-info tbk-flex tbk-flex-col tbk-items-center">
          {infoModalTitle !== "Referral Bonus Applied" ? (
            <button
              onClick={closeInfoModal}
              className="tbk-absolute tbk-right-2 tbk-top-2 tbk-z-10 tbk-hidden tbk-text-coral"
            >
              <CloseIcon />
            </button>
          ) : null}
          {infoModalTitle === "Referral Bonus Applied" ? (
            <CheckNiceIcon size={64} className="tbk-text-green" />
          ) : null}
          <h3 className="tbk-text-title-bold tbk-mb-1 tbk-mt-0 tbk-text-center tbk-text-grey-main">
            {infoModalTitle}
          </h3>
          <p className="tbk-text-main tbk-mb-4 tbk-max-w-full tbk-text-center tbk-text-grey-main">
            {infoModalMsg}
          </p>
          {error &&
          infoModalTitle !== "Referral Bonus Applied" &&
          infoModalTitle !== "Ambassador Link Activated" ? (
            <BlockerIcon size={64} className="tbk-mb-4 tbk-text-green" />
          ) : (
            infoModalTitle !== "Referral Bonus Applied" && (
              <CheckNiceIcon size={64} className="tbk-mb-4 tbk-text-green" />
            )
          )}
          {infoModalTitle !== "Referral Bonus Applied" ? (
            <Button className="tbk-w-full" onClick={closeInfoModal}>
              Ok
            </Button>
          ) : null}
        </div>
      </Modal>
    </>
  );
}
